'use client';
import type { ThemeOptions } from '@mui/material';
import { createTheme } from '@mui/material';
import { grey } from '@mui/material/colors';

/*

#5624D0 (Base Purple) - Your original color
#6B3DDE (Lighter Purple) - Good for hover states
#4310BC (Darker Purple) - For contrast elements
#3C1694 (Deep Purple) - For more serious sections
#7E5AE9 (Lavender) - A softer accent
#9B7EFC (Soft Lilac) - Great for highlights
#402E8A (Muted Purple) - More subdued option
#24097A (Royal Purple) - Rich and deep variant
*/

const getTheme = (mode: 'light' | 'dark' = 'light') =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: '#5624d0', // Udemy purple
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#24097A',
      },
      background: {
        // default: mode === 'dark' ? '#1c1d1f' : '#ffffff',
        // paper: mode === 'dark' ? '#2d2f31' : '#f7f9fa',
        default: mode === 'dark' ? grey[900] : grey[50],
        paper: mode === 'dark' ? grey[800] : grey[200],
      },
      text: {
        primary: mode === 'dark' ? '#ffffff' : '#1c1d1f',
        secondary: mode === 'dark' ? '#d1d1d1' : '#4d4f51',
      },
      error: {
        main: '#e01c3d',
      },
      warning: {
        main: '#f59e0b',
      },
      success: {
        main: '#10b981',
      },
      info: {
        main: '#3b82f6',
      },
    },
    typography: {
      fontFamily: `'Helvetica Neue', 'Roboto', 'Arial', sans-serif`,
      h1: {
        fontWeight: 700,
        fontSize: '2.25rem',
      },
      h2: {
        fontWeight: 600,
        fontSize: '1.75rem',
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.5rem',
      },
      subtitle1: {
        fontWeight: 500,
        fontSize: '1rem',
      },
      button: {
        fontWeight: 600,
        textTransform: 'none',
      },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 8,
            // 1px solid border using the “divider” color from the palette
            border: `1px solid ${theme.palette.divider}`,
            boxShadow:
              mode === 'dark'
                ? '0 2px 12px rgba(0,0,0,0.4)'
                : '0 2px 6px rgba(0,0,0,0.1)',
          }),
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            'borderRadius': 6,
            'fontWeight': 600,
            'padding': '8px 16px',
            'boxShadow': 'none',
            '&:hover': {
              boxShadow: '0 0 0 4px rgba(86, 36, 208, 0.1)',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#2d2f31' : '#ffffff',
            color: mode === 'dark' ? '#fff' : '#1c1d1f',
            boxShadow:
              mode === 'dark'
                ? '0 1px 2px rgba(255,255,255,0.05)'
                : '0 1px 3px rgba(0,0,0,0.1)',
          },
        },
      },
    },
  });

const theme = createTheme(getTheme('light') as ThemeOptions);

export default theme;
